/**
 * Typed fetch client (docs/03-architecture.md §4.3). Cookie-based sessions (`credentials:
 * 'include'`), the `X-Requested-With` header our API's CSRF check requires on mutations, JSON
 * envelope parsing, and a single automatic refresh-on-401 retry.
 */

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;
  readonly requestId?: string;

  constructor(
    code: string,
    status: number,
    message: string,
    details?: unknown,
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.requestId = requestId;
  }
}

const BASE_URL = '/api/v1';
const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

interface ErrorEnvelope {
  error: { code: string; message: string; details?: unknown; requestId?: string };
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function isErrorEnvelope(value: unknown): value is ErrorEnvelope {
  return typeof value === 'object' && value !== null && 'error' in value;
}

/** Share refresh rotation when several queries encounter an expired access cookie together. */
let refreshPromise: Promise<void> | undefined;
function refreshSession(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = request<void>('/auth/refresh', { method: 'POST' }, false).finally(() => {
      refreshPromise = undefined;
    });
  }
  return refreshPromise;
}

export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

async function request<T>(path: string, init: RequestInit, allowRefresh: boolean): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  // A FormData body needs the browser to set its own multipart boundary — never override it.
  if (init.body !== undefined && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (MUTATING.has(method)) headers.set('X-Requested-With', 'FinBob');

  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    method,
    headers,
    credentials: 'include',
  });

  // One retry after a silent refresh — never for the refresh call itself or the initial sign-in.
  if (
    res.status === 401 &&
    allowRefresh &&
    path !== '/auth/refresh' &&
    !path.startsWith('/auth/google')
  ) {
    // The retried request supersedes this response — its body is never read, which otherwise
    // leaves the browser treating the connection as still loading indefinitely (observed
    // blocking Playwright's `networkidle` wait in ui-check on every signed-out page load).
    await res.body?.cancel();
    // Propagate refresh outages as outages, rather than the original expired-access 401.
    await refreshSession();
    return request<T>(path, init, false);
  }

  const body = await parseBody(res);
  if (!res.ok) {
    if (isErrorEnvelope(body)) {
      throw new ApiError(
        body.error.code,
        res.status,
        body.error.message,
        body.error.details,
        body.error.requestId,
      );
    }
    throw new ApiError('UNKNOWN', res.status, res.statusText || 'request failed');
  }
  return body as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path, {}, true),
  post: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(
      path,
      { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) },
      true,
    ),
  /** Multipart form upload — `request()` only sets `Content-Type: application/json` when it picks
   * the header itself, so a caller-supplied `FormData` body (which needs its own multipart
   * boundary, set by the browser) is left alone. */
  upload: <T>(path: string, form: FormData): Promise<T> =>
    request<T>(path, { method: 'POST', body: form }, true),
  patch: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(
      path,
      { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) },
      true,
    ),
  put: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(
      path,
      { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) },
      true,
    ),
  // `body` is optional — every existing DELETE targets an :id in the path and needs none; push
  // subscriptions are the first to unsubscribe by endpoint (a URL, awkward to path-encode) in a
  // body instead, same as the other verbs above.
  delete: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(
      path,
      { method: 'DELETE', body: body === undefined ? undefined : JSON.stringify(body) },
      true,
    ),
};

/** A plain navigation (not fetch) — OAuth requires a real top-level redirect to Google. */
export function navigateToGoogleSignIn(): void {
  window.location.href = `${BASE_URL}/auth/google`;
}
